import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatIconModule } from '@angular/material/icon';
import { Utils } from '@services';
import { Observable } from 'rxjs';
import { ButtonComponent } from '../button/button.component';
import { SpinnerComponent } from '../spinner/spinner.component';

export interface PdfPreviewDialogData {
  name: string;
  blob$: Observable<Blob>;
}

@Component({
  selector: 'icaredx-pdf-preview-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, ButtonComponent, SpinnerComponent],
  templateUrl: './pdf-preview-dialog.component.html',
})
export class PdfPreviewDialogComponent {
  private readonly dialogRef = inject<DialogRef<void>>(DialogRef);
  protected readonly data = inject<PdfPreviewDialogData>(DIALOG_DATA);
  private readonly sanitizer = inject(DomSanitizer);

  protected readonly loading = signal(true);
  protected readonly error = signal(false);
  protected readonly src = signal<SafeResourceUrl | null>(null);
  protected readonly imageUrl = signal<string | null>(null);
  protected readonly isImage = signal(false);

  private blob: Blob | null = null;
  private objectUrl: string | null = null;

  constructor() {
    this.data.blob$.pipe(takeUntilDestroyed()).subscribe({
      next: blob => {
        this.blob = blob;
        this.objectUrl = URL.createObjectURL(blob);
        const isImage =
          blob.type.startsWith('image/') ||
          /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(this.data.name);
        this.isImage.set(isImage);
        if (isImage) {
          this.imageUrl.set(this.objectUrl);
        } else {
          const viewerUrl = `${this.objectUrl}#navpanes=0&pagemode=none`;
          this.src.set(
            this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl)
          );
        }
        this.loading.set(false);
      },
      error: () => {
        this.error.set(true);
        this.loading.set(false);
      },
    });

    inject(DestroyRef).onDestroy(() => {
      if (this.objectUrl) {
        URL.revokeObjectURL(this.objectUrl);
      }
    });
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected download(): void {
    if (this.blob) {
      Utils.downloadBlob(this.blob, this.data.name);
    }
  }
}
